import React, { useMemo, useState } from 'react';
import { GenericDropdownOption } from '../../../../../../App.types';
import styles from './TiktokAudios.module.css';
import useUrlState from '../../../../../../Hooks/useUrlState';
import Button from '../../../../../../ui/Buttons/Button/Button';
import { ProtectedByUserGroups } from '../../../../../Auth/components/ProtectedByUserGroup/ProtectedByUserGroups';
import AddNewSongModal from './AddNewSongModal/AddNewSongModal';
import { ExploreTiktokAudiosTableRowV2 } from '../../../../TikTok.types';
import { DebounceInput } from 'react-debounce-input';
import { StylesConfig, ValueType } from 'react-select';
import Select from '../../../../../../ui/DataEntry/Select/Select';
import cn from 'classnames';
import useNonNullContext from '../../../../../../Hooks/useNonNullContext';
import { OptionsContext } from '../../../../../../contexts/Options';
import { useTiktokAudios } from './useTiktokAudios';
import TiktokAudiosTable from './TiktokAudiosTable/TiktokAudiosTable';
import TiktokAudioModal from '../../details/TiktokAudioModal/TiktokAudioModal';
import SearchInput from '../../../../../../ui/DataEntry/SearchInput/SearchInput';
import { getTikTokAudioIdFromUrl, isValidTikTokAudioIdentifier } from '../../../../helpers';
import ReactTooltip from 'react-tooltip';
import { ReactComponent as InfoIcon } from '../../../../../../assets/InfoCircle.svg';

type UrlState = {
    page: number;
    pageSize: number;
    search: string;
    tiktokId: string;
    orderByDesc?: string;
    min_video_count?: number;
    max_video_count?: number;
    soundType?: boolean | undefined;
    audioModal?: number;
};

const soundTypeOptions: GenericDropdownOption<boolean | undefined>[] = [
    { label: 'All', value: undefined },
    { label: 'Official', value: false },
    { label: 'Original', value: true },
];

const soundTypeSelectStyles: StylesConfig = {
    control: (base) => ({
        ...base,
        border: '1px solid #c2cfe0',
        borderRadius: '0.375rem',
    }),
    valueContainer: (base) => ({
        ...base,
        padding: '0.5rem',
        fontWeight: 'normal',
        minWidth: '5.5rem',
        fontSize: '0.85rem',
    }),
    menu: (base) => ({
        ...base,
        fontSize: '0.85rem',
    }),
    dropdownIndicator: (base) => ({
        ...base,
        padding: '0.2rem',
        paddingLeft: 0,
    }),
};

const sortingOptions: GenericDropdownOption<string>[] = [
    { label: 'Total Videos', value: '-video_count' },
    { label: 'Daily Growth (# videos)', value: '-video_count_daily_change' },
    { label: 'Daily Growth (% uplift)', value: '-video_count_daily_change_relative' },
    { label: 'Weekly Growth (# videos)', value: '-video_count_weekly_change' },
    { label: 'Weekly Growth (% uplift)', value: '-video_count_weekly_change_relative' },
];

const sortingSelectStyles: StylesConfig = {
    control: (base) => ({
        ...base,
        borderColor: '#C2CFE0',
    }),
    valueContainer: (base) => ({
        ...base,
        minWidth: '10rem',
    }),
    singleValue: (base) => ({
        ...base,
        color: '#58698E',
        fontWeight: 600,
        fontSize: '0.75rem',
    }),
    menuList: (base) => ({
        ...base,
        color: '#58698E',
        fontWeight: 600,
        fontSize: '0.75rem',
    }),
};

const TiktokAudios = () => {
    const { clients } = useNonNullContext(OptionsContext);

    const [showNewSongModal, setShowNewSongModal] = useState(false);
    const [urlState, setUrlState] = useUrlState<UrlState>({
        page: 1,
        pageSize: 25,
        search: '',
        orderByDesc: '-video_count_daily_change',
        min_video_count: 100,
        tiktokId: '',
    });

    const page = Number(urlState.page);
    const setPage = (page: number) => setUrlState({ page });
    const pageSize = Number(urlState.pageSize ?? 10);
    const setPageSize = (pageSize: number | undefined) => setUrlState({ pageSize: pageSize ?? 10 });
    const search = urlState.search ?? '';
    const setSearch = (search: string) => setUrlState({ search, page: 1 });
    const orderByDesc = urlState.orderByDesc;
    const setSorting = (orderByDesc: string | undefined) => setUrlState({ orderByDesc });
    const minVideoCount = Number(urlState.min_video_count) || undefined;
    const setMinVideoCount = (e: React.ChangeEvent<HTMLInputElement>) =>
        setUrlState({ min_video_count: Number(e.target.value) || undefined, page: 1 });
    const maxVideoCount = Number(urlState.max_video_count) || undefined;
    const setMaxVideoCount = (e: React.ChangeEvent<HTMLInputElement>) =>
        setUrlState({ max_video_count: Number(e.target.value) || undefined, page: 1 });
    const soundType = soundTypeOptions.find((type) => String(type.value) === String(urlState.soundType));
    const setSoundType = (option: ValueType<GenericDropdownOption<boolean | undefined>, false>) =>
        setUrlState({ soundType: option?.value, page: 1 });
    const audioIdForModal = Number(urlState.audioModal);
    const setAudioIdForModal = (id: number) => setUrlState({ audioModal: id });
    const tiktokId = urlState.tiktokId ?? '';
    const setTiktokId = (e: React.ChangeEvent<HTMLInputElement>) => setUrlState({ tiktokId: e.target.value });

    const {
        tiktokAudioStats,
        statsCount,
        tiktokAudioAssets,
        clientMonitoredAudioRelations,
        linkedSongs,
        isLoading,
        hasError,
        handleAudioAddedToCollection,
        handleAudioRemovedFromCollection,
        handleUpdateClientMonitoredAudioRelation,
        handleAudioLinked,
    } = useTiktokAudios({
        search,
        tiktok_sound_ids: isValidTikTokAudioIdentifier(tiktokId) ? getTikTokAudioIdFromUrl(tiktokId) : undefined,
        ordering: orderByDesc,
        page,
        page_size: pageSize,
        min_video_count: minVideoCount,
        max_video_count: maxVideoCount,
        is_original: soundType?.value,
    });

    const noDataLabel = hasError ? 'Sorry, we are experiencing technical issues' : 'No audios found';

    const rows = useMemo<ExploreTiktokAudiosTableRowV2[]>(
        () =>
            tiktokAudioStats.map((stats) => {
                const asset = tiktokAudioAssets.find((asset) => asset.audio_id === stats.music_id);
                return {
                    ...stats,
                    image_url: asset?.image_thumb.cached_url || asset?.image_thumb.original_url,
                    audioUrl: asset?.audio_file.cached_url || asset?.audio_file.original_url,
                    dailyGrowth: stats.standardised_data.daily_video_counts.slice(-7).reduce(
                        (acc, current) => {
                            acc.push({
                                timestamp: current.timestamp,
                                value: current.change,
                            });

                            return acc;
                        },
                        [] as ExploreTiktokAudiosTableRowV2['dailyGrowth']
                    ),
                    clientMonitoredAudioRelation: clientMonitoredAudioRelations
                        .filter((rel) => rel.monitored_audio_id === stats.monitored_music_id)
                        .map((rel) => {
                            return {
                                ...rel,
                                client_name: clients.find((c) => c.id === rel.client_id)?.name ?? '',
                            };
                        }),
                    linkedSongs: linkedSongs.filter((s) => s.tiktok_audios.includes(stats.music_id)),
                };
            }),
        [clientMonitoredAudioRelations, clients, tiktokAudioStats, tiktokAudioAssets, linkedSongs]
    );

    const selectedMonitoredTiktokAudioStats = useMemo(
        () => rows.find((row) => row.music_id === audioIdForModal),
        [audioIdForModal, rows]
    );

    const selectedSortingValue = sortingOptions.find((option) => option.value === orderByDesc);

    return (
        <div className={styles.container}>
            <p className={styles.title}>TikTok Audio Search</p>
            <div className={styles.header}>
                <div className={styles.headerLeft}>
                    <SearchInput className={styles.searchInput} value={search} onChange={setSearch} />
                    <div className={cn(styles.filterField, styles.tiktokId)}>
                        <label
                            className={styles.filterLabel}
                            htmlFor="tiktokId"
                            aria-invalid={!isValidTikTokAudioIdentifier(tiktokId)}
                            data-for="tiktokId"
                            data-tip={
                                !isValidTikTokAudioIdentifier(tiktokId) ? 'TikTok Audio ID is invalid' : undefined
                            }
                        >
                            {!isValidTikTokAudioIdentifier(tiktokId) && (
                                <>
                                    <InfoIcon className={styles.filterLabelIcon} />
                                    <ReactTooltip id="tiktokId" />
                                </>
                            )}
                            TikTok ID
                        </label>
                        <DebounceInput
                            id="tiktokId"
                            className={styles.filterInput}
                            debounceTimeout={500}
                            value={tiktokId}
                            onChange={setTiktokId}
                            placeholder="TikTok Audio ID or URL"
                        />
                    </div>

                    <div className={styles.filterField}>
                        <label className={styles.filterLabel} htmlFor="minVideoCount">
                            Min video creations
                        </label>
                        <DebounceInput
                            id="minVideoCount"
                            type="number"
                            className={styles.filterInput}
                            debounceTimeout={500}
                            value={minVideoCount}
                            onChange={setMinVideoCount}
                        />
                    </div>

                    <div className={styles.filterField}>
                        <label className={styles.filterLabel} htmlFor="maxVideoCount">
                            Max video creations
                        </label>
                        <DebounceInput
                            placeholder="Unlimited"
                            id="maxVideoCount"
                            type="number"
                            className={styles.filterInput}
                            debounceTimeout={500}
                            value={maxVideoCount}
                            onChange={setMaxVideoCount}
                        />
                    </div>

                    <div className={cn(styles.filterField, styles.soundTypeField)}>
                        <label className={styles.filterLabel} htmlFor="audioType">
                            Audio type
                        </label>
                        <Select
                            id="audioType"
                            className={styles.soundTypeSelect}
                            isSearchable={false}
                            styles={soundTypeSelectStyles}
                            value={soundType}
                            options={soundTypeOptions}
                            onChange={setSoundType}
                            components={{
                                IndicatorSeparator: null,
                            }}
                        />
                    </div>
                </div>

                <div className={styles.headerRight}>
                    <label className={styles.label}>Sort by</label>
                    <div data-test-id="sort-container">
                        <Select
                            isSearchable={false}
                            styles={sortingSelectStyles}
                            value={selectedSortingValue}
                            options={sortingOptions}
                            onChange={(option) => {
                                setSorting(option?.value);
                            }}
                        />
                    </div>
                    <ProtectedByUserGroups groups={['explorer_tiktok_editor']}>
                        <Button type="filled" color="black" onClick={() => setShowNewSongModal(true)}>
                            Track new audio
                        </Button>

                        <AddNewSongModal
                            closeOnOverlayClick
                            clients={clients}
                            isOpen={showNewSongModal}
                            onClose={() => setShowNewSongModal(false)}
                        />
                    </ProtectedByUserGroups>
                </div>
            </div>
            <TiktokAudiosTable
                noDataLabel={noDataLabel}
                loading={isLoading}
                data={rows}
                count={statsCount}
                page={page}
                setPage={setPage}
                pageSize={pageSize}
                setPageSize={setPageSize}
                orderByDesc={orderByDesc}
                onAudioAddedToCollection={handleAudioAddedToCollection}
                onAudioRemovedFromCollection={handleAudioRemovedFromCollection}
                onAudioLinked={handleAudioLinked}
                onRowClick={(row) => setAudioIdForModal(row.music_id)}
            />

            <TiktokAudioModal
                isModalOpen={!isNaN(audioIdForModal)}
                closeModal={() => setUrlState({ audioModal: undefined })}
                audioId={audioIdForModal}
                monitoredAudioStats={selectedMonitoredTiktokAudioStats}
                onAlternateNameUpdated={handleUpdateClientMonitoredAudioRelation}
            />
        </div>
    );
};

export default TiktokAudios;
